import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Download, Loader2, Mic } from 'lucide-react';
import { resolveDownloadUrl, fetchAsBlob } from '../../services/storageService';
import { TYPE } from '../../constants/typeScale';

/** mm:ss for the scrubber time labels. */
const fmt = (s: number) => {
  const t = Number.isFinite(s) && s > 0 ? s : 0;
  return `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
};

/**
 * A persistent voice-message bubble (WhatsApp / WeChat / Line style) for an agent message that already has a
 * generated voice-over. The audio is STORED, so play/replay is FREE — no re-charge, no confirm-spend. Offers
 * play/pause, a scrubber + time, and a download/save. Resolves the storage path to a URL once on mount.
 */
const VoiceMessageBubble: React.FC<{ voicePath: string; mime?: string }> = ({ voicePath, mime }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const u = await resolveDownloadUrl(voicePath);
        if (alive) setUrl(u);
      } catch { /* leave url null → the controls stay disabled (rare: the asset moved/expired) */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [voicePath]);

  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (a.paused) void a.play().catch(() => { /* autoplay/gesture edge — user can tap again */ }); else a.pause();
  };
  const seek = (v: number) => { const a = audioRef.current; if (a && Number.isFinite(v)) { a.currentTime = v; setCur(v); } };

  const download = async () => {
    if (!url || downloading) return;
    setDownloading(true);
    try {
      const blob = await fetchAsBlob(url);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `voice-message.${(mime || '').includes('wav') ? 'wav' : 'mp3'}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch { /* best-effort */ } finally { setDownloading(false); }
  };

  return (
    <div className="mt-1.5 inline-flex w-64 max-w-full items-center gap-2.5 rounded-2xl border border-secondary-200 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-800/60 px-3 py-2">
      <button onClick={toggle} disabled={loading || !url} aria-label={playing ? 'Pause' : 'Play voice'}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-900 text-white dark:bg-white dark:text-secondary-900 disabled:opacity-40">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-px" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className={`flex items-center gap-1 ${TYPE.meta} text-secondary-400`}><Mic className="h-3 w-3" /> Voice message</div>
        <input type="range" min={0} max={dur || 0} step={0.1} value={Math.min(cur, dur || 0)} onChange={(e) => seek(Number(e.target.value))}
          disabled={!url || !dur} aria-label="Seek"
          className="mt-1 h-1 w-full cursor-pointer appearance-none rounded-full bg-secondary-200 accent-secondary-900 dark:bg-secondary-700 dark:accent-white disabled:opacity-60" />
        <div className={`mt-0.5 ${TYPE.meta} tabular-nums text-secondary-400`}>{fmt(cur)} / {fmt(dur)}</div>
      </div>
      <button onClick={() => void download()} disabled={!url || downloading} aria-label="Download voice message" title="Save to your device"
        className="shrink-0 rounded-full p-1.5 text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 disabled:opacity-40">
        {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      </button>
      {url && (
        <audio ref={audioRef} src={url} preload="metadata"
          onLoadedMetadata={(e) => setDur((e.currentTarget as HTMLAudioElement).duration || 0)}
          onTimeUpdate={(e) => setCur((e.currentTarget as HTMLAudioElement).currentTime || 0)}
          onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setCur(0); }} />
      )}
    </div>
  );
};

export default VoiceMessageBubble;
